
import type {ComponentProps, ComponentType, ReactNode} from 'react';
import React from 'react';
import {withCssModule} from '@pb/kit-util-with-css-module';
import styles from './input.component.module.styl';
import {convertPxToRem} from '@pb/kit-util-convert-px-to-rem';
import {useAdornmentWidth} from './utils/use-adornment-width';
import {useCustomGroupItemContext} from './custom-group/custom-group-item-context';


import type {InputComponentProps} from './typedef';

const classNames = withCssModule(styles);

const TYPE_CLASSES = {
    SUCCESS: 'input__root_success',
    ERROR: 'input__root_error',
    DEFAULT: '',
};

export type InputProps<T extends InputComponentProps = InputComponentProps> = T & {
    /**
     * Ref to the outer `<div>` wrapper element.
     */
    rootRef?: React.Ref<HTMLDivElement>,
    /**
     * Native `<div>` props spread onto the root wrapper element. Use to add
     * layout classes, `data-*` attributes, or event handlers to the container.
     */
    rootProps?: ComponentProps<'div'>,
    /**
     * Visual validation state controlling the border color:
     * - `"DEFAULT"` — neutral, no color indicator (default).
     * - `"SUCCESS"` — green border, for confirmed valid input.
     * - `"ERROR"` — red border; also sets `aria-invalid="true"` on the input.
     */
    color?: 'SUCCESS' | 'ERROR' | 'DEFAULT',
    /**
     * Node rendered inside the left adornment slot, overlaid on the input.
     * The input's left padding is automatically expanded to avoid overlap.
     * Commonly used for search icons, currency symbols, or flag icons.
     */
    startAdornment?: ReactNode,
    /**
     * Native `<div>` props spread onto the left adornment wrapper. Use
     * `className` to position or style the adornment independently.
     */
    startAdornmentProps?: ComponentProps<'div'>,
    /**
     * Node rendered inside the right adornment slot, overlaid on the input.
     * The input's right padding is automatically expanded to avoid overlap.
     * Commonly used for clear buttons, unit labels, or status icons.
     */
    endAdornment?: ReactNode,
    /**
     * Native `<div>` props spread onto the right adornment wrapper.
     */
    endAdornmentProps?: ComponentProps<'div'>,
    /**
     * Replacement for the native `<input>` element. Pass a custom component
     * that accepts `InputComponentProps` when integrating with third-party
     * masked-input or autocomplete libraries.
     */
    inputComponent?: ComponentType<T>,

    /**
     * When `true`, suppresses the read-only CSS styles (grey background,
     * non-interactive cursor) while keeping the `readOnly` attribute on the
     * input. Use when the read-only state is handled visually elsewhere.
     */
    unstyledReadonly?: boolean,
};

/**
 * Foundation text field with start/end adornment slots, validation color
 * states, and support for custom input components.
 *
 * Wraps a native `<input>` (or custom `inputComponent`) inside a `<div>`
 * container. The left/right padding of the input is automatically calculated
 * from the measured width of any adornment elements so content never overlaps.
 *
 * Variant sub-components in this package:
 * - `TypedInput` — adds format validation and an error message.
 * - `SuffixedInput` — appends a fixed suffix string inside the input value.
 * - `DataTypedInput` — locale-aware input for numbers, dates, and date ranges.
 *
 * ## Accessibility
 * Accepts all native `<input>` attributes. Pair with `aria-label` /
 * `aria-labelledby` and `aria-describedby` per the HTML forms spec.
 * `aria-invalid` is set automatically when `color="ERROR"`.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-input}
 */
export function Input<T extends InputComponentProps>(props: InputProps<T>) {
    const {
        rootRef,
        rootProps,
        color = 'DEFAULT',
        startAdornment,
        startAdornmentProps,
        endAdornment,
        endAdornmentProps,
        unstyledReadonly,
        inputComponent,
        ...nativeProps
    } = props;

    const InputComponent = inputComponent || 'input' as unknown as ComponentType<T>;

    const {inputId} = useCustomGroupItemContext();

    const [leftAdornmentWidth, leftAdornmentRef] = useAdornmentWidth();
    const [rightAdornmentWidth, rightAdornmentRef] = useAdornmentWidth();

    return (
        <div
            ref={rootRef}
            {...rootProps}
            className={classNames('input__root', rootProps?.className, TYPE_CLASSES[color], {
                input__root_uneditable: nativeProps.readOnly && !unstyledReadonly,
                input__root_disabled: nativeProps.disabled,
            })}>
            {startAdornment ? (
                <div
                    {...startAdornmentProps}
                    ref={leftAdornmentRef}
                    className={classNames('input__adornment', 'input__adornment_left', startAdornmentProps?.className)}>
                    {startAdornment}
                </div>
            ) : null}
            <InputComponent
                autoComplete="off"
                {...nativeProps as T}
                placeholder={nativeProps.placeholder || ' '} // to detect :placeholder-shown in CSS
                id={nativeProps?.id || inputId}
                className={classNames('input__component', nativeProps.className)}
                aria-invalid={nativeProps['aria-invalid'] ?? color === 'ERROR'}
                onFocus={e => {
                    nativeProps.onFocus?.(e);
                }}
                onBlur={e => {
                    nativeProps.onBlur?.(e);
                }}
                style={{
                    ...nativeProps.style,
                    paddingLeft: leftAdornmentWidth ? convertPxToRem(leftAdornmentWidth + 8) : undefined,
                    paddingRight: rightAdornmentWidth ? convertPxToRem(rightAdornmentWidth + 8) : undefined,
                }}
            />
            {endAdornment ? (
                <div
                    {...endAdornmentProps}
                    ref={rightAdornmentRef}
                    className={classNames('input__adornment', 'input__adornment_right', endAdornmentProps?.className)}>
                    {endAdornment}
                </div>
            ) : null}
        </div>
    );
}

Input.displayName = 'Input';
